import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FileText, Loader2, Plus } from "lucide-react";
import { formatINR } from "@/lib/utils";
import { QuoteStatusBadge } from "@/pages/quotes/QuoteStatusBadge";
import type { QuotationRecord } from "@/types";

interface DealQuotesCardProps {
  dealId: string;
  isClosed: boolean;
}

export function DealQuotesCard({ dealId, isClosed }: DealQuotesCardProps) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [quotes, setQuotes] = useState<QuotationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const canRead = hasPermission("quote.read");
  const canCreate = hasPermission("quote.create") && !isClosed;

  useEffect(() => {
    if (!canRead) return;
    api
      .get(`/api/Quotation/List?deal_id=${dealId}&page_size=100`)
      .then((res) => setQuotes(unwrapPage<QuotationRecord>(res).rows))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [dealId, canRead]);

  if (!canRead) return null;

  const createQuote = async () => {
    setCreating(true);
    try {
      const res: any = await api.post("/api/Quotation/Insert", { deal_id: dealId });
      success("Quote Created", "Draft quote created from this deal's products");
      navigate(`/quotes/${res?.AddtionalData}`);
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create quote");
    } finally {
      setCreating(false);
    }
  };

  // Superseded versions are reachable from the newer version; hide them here
  const visible = quotes.filter((q) => q.quote_status !== "SUPERSEDED");

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Quotes</h3>
          {visible.length > 0 && (
            <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">{visible.length}</span>
          )}
        </div>
        {canCreate && (
          <Button size="sm" variant="outline" onClick={createQuote} disabled={creating} className="h-8 gap-1.5 text-xs">
            {creating ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            Create Quote
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <div className="py-5 px-4 text-center text-xs text-muted-foreground">
          No quotes yet.{canCreate && " Create one from this deal's products."}
        </div>
      ) : (
        <ul className="divide-y">
          {visible.map((q) => (
            <li key={q.id}>
              <button
                type="button"
                onClick={() => navigate(`/quotes/${q.id}`)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/50"
              >
                <div>
                  <div className="font-mono text-xs font-semibold">{q.display_number}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Valid until{" "}
                    {new Date(`${q.valid_until.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{formatINR(q.grand_total)}</span>
                  <QuoteStatusBadge status={q.quote_status} expired={q.is_expired} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
